import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DialogContextValues } from 'common/types/dialogContext';
import { ConfirmationApproveForClientsOrDraft } from 'components/Dialogs';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { DialogContext } from 'context';
import {
  APPROVE_FOR_CLIENT_ACCESS_LABEL,
  APPROVE_MESSAGE,
  DRAFT_MESSAGE,
  MARK_AS_DRAFT_LABEL,
  REPORT_APPROVED_FOR_CLIENT_ACCESS,
  REPORT_MARKED_AS_DRAFT,
  SAVE_LABEL,
  TOGGLE_EDIT_MODE_LABEL,
} from 'dashboard409a/common/constants/messages/valuationMethodsDetailTable';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext/CompanyDashboardContext';
import { hideLoadingProgressBar, showLoadingProgressBar } from 'dashboard409a/states/features/global';
import {
  approveReportForClient,
  findValuationScalarOpinion,
  getValuationScalarOpinion,
  IValuationState,
  updateValuationScalarOpinion,
} from 'dashboard409a/states/features/valuation';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import { useResponse } from 'services/hooks';
import { 
  LESS_DEBT_ALIAS,
  VALUE_PER_COMMON_SHARE2_ALIAS,
  VALUE_PER_COMMON_SHARE_ALIAS, 
  WEIGHTED_ENTERPRISE_MARKET_VALUE_ALIAS,
} from '../config/rowConfig';
import { ValuationMethodsDetailWorkbook } from '../config/types';
import useValuationMethodsDetailTableData from '../hooks/useValuationMethodsDetailTableData';
import { ValuationMethodsDetailTableProps } from '../types';
import ApproveForClientsDialogWrapper from './ApproveForClientsDialogWrapper';

const ValuationMethodsDetailTable: FC<ValuationMethodsDetailTableProps> = props => {
  const { data, isLoading, format, formatDispatch, updatedValuationScalarOpinion, setUpdatedValuationScalarOpinion }
    = props;
  const [formState, setFormState] = useState({
    isValid: true,
  });

  const { spreadsheet: valuationMethodsDetailSheet } = useValuationMethodsDetailTableData({ data, format });
  const { processErrorResponse, successNotification } = useResponse();
  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;
  const { currentValuationScalarOpinion } = useSelector<RootStateType, IValuationState>(state => state.valuation);
  const isApprovedForClients = currentValuationScalarOpinion?.approved_for_clients ?? false;

  const [spreadsheets, setSpreadsheets] = useState([valuationMethodsDetailSheet]);
  const dispatch = useDispatch<AppDispatchType>();

  const { updatePageActions, setEditMode, editMode } = useCompanyDashboardContext();
  const { currentCompanyMeasurementDateId, currentCompany } = useSelector((state: RootStateType) => state.global);

  const { onChange, workbook, cells } = useWorkbook(spreadsheets) as unknown as ValuationMethodsDetailWorkbook;

  const validateCells = useCallback(() => {
    if (!cells?.ValuationMethodsDetail) {
      return true;
    }

    const cellsToValidate = [
      { alias: WEIGHTED_ENTERPRISE_MARKET_VALUE_ALIAS, value: cells.ValuationMethodsDetail.weighted_market_enterprise_value?.value },
      { alias: LESS_DEBT_ALIAS, value: cells.ValuationMethodsDetail.less_debt?.value },
      { alias: VALUE_PER_COMMON_SHARE_ALIAS, value: cells.ValuationMethodsDetail.marketable_value_per_common_share?.value },
      { alias: VALUE_PER_COMMON_SHARE2_ALIAS, value: cells.ValuationMethodsDetail.discount_for_lack_of_marketability?.value },
    ];

    return cellsToValidate.every(cell => {
      const value = Number(cell.value);
      return !Number.isNaN(value) && value >= 0;
    });
  }, [cells]);

  const updateEnterpriseValueCard = useCallback(
    (cell, value) => {
      if (cell.alias === WEIGHTED_ENTERPRISE_MARKET_VALUE_ALIAS) {
        setUpdatedValuationScalarOpinion({
          ...updatedValuationScalarOpinion,
          weighted_market_enterprise_value: value,
        });
      } else if (cell.alias === VALUE_PER_COMMON_SHARE_ALIAS) {
        setUpdatedValuationScalarOpinion({
          ...updatedValuationScalarOpinion,
          marketable_value_per_common_share: value,
        });
      }

      onChange(cell, value);
      setFormState({ isValid: validateCells() });
    },
    [onChange, setUpdatedValuationScalarOpinion, updatedValuationScalarOpinion, validateCells]
  );

  const valuationMethodsDetailSheetProps = valuationMethodsDetailSheet
    ? {
      ...valuationMethodsDetailSheet,
      sheet: valuationMethodsDetailSheet,
      workbook,
      onChange: updateEnterpriseValueCard,
      page: 'Valuation Methods Detail',
      format,
      formatDispatch,
      data: valuationMethodsDetailSheet.data,
    }
    : null;

  const handleSave = useCallback(() => {
    dispatch(showLoadingProgressBar());
    if (!currentCompanyMeasurementDateId) {
      dispatch(hideLoadingProgressBar());
      return;
    }

    const valuationMethodsDetailSheetPayload = {
      weighted_market_enterprise_value: Number(cells?.ValuationMethodsDetail.weighted_market_enterprise_value.value).toFixed(2),
      less_debt: Number(cells?.ValuationMethodsDetail.less_debt.value).toFixed(2),
      marketable_value_per_common_share: Number(cells?.ValuationMethodsDetail.marketable_value_per_common_share.value).toFixed(2),
      discount_for_lack_of_marketability: Number(cells?.ValuationMethodsDetail.discount_for_lack_of_marketability.value).toFixed(4),
    };

    dispatch(
      updateValuationScalarOpinion({
        dateId: currentCompanyMeasurementDateId,
        data: valuationMethodsDetailSheetPayload,
      })
    )
      .then(() => {
        successNotification('Valuation Methods table updated successfully');
        if (currentCompany?.id) {
          dispatch(getValuationScalarOpinion(currentCompany.id));
        }
      })
      .catch(error => {
        processErrorResponse(error);
      })
      .finally(() => {
        dispatch(hideLoadingProgressBar());
      });
  }, [currentCompanyMeasurementDateId, currentCompany?.id, dispatch, cells, processErrorResponse, successNotification]);

  const handleApproveReportForClientAccess = useCallback(() => {
    showDialog({
      wrapper: ApproveForClientsDialogWrapper,
      wrapperProps: {
        isApprovedForClients,
      },
      content: ConfirmationApproveForClientsOrDraft,
      contentProps: {
        customMessage: isApprovedForClients ? DRAFT_MESSAGE : APPROVE_MESSAGE,
      },
      actions: [
        {
          label: 'Cancel',
          type: 'cancel',
        },
        {
          label: isApprovedForClients ? MARK_AS_DRAFT_LABEL : APPROVE_FOR_CLIENT_ACCESS_LABEL,
          variant: 'contained',
          type: 'primary',
          callback: () => {
            dispatch(showLoadingProgressBar());
            if (!currentCompanyMeasurementDateId) {
              dispatch(hideLoadingProgressBar());
              return;
            }

            dispatch(approveReportForClient(currentCompanyMeasurementDateId))
              .then(() => {
                successNotification(isApprovedForClients ? REPORT_MARKED_AS_DRAFT : REPORT_APPROVED_FOR_CLIENT_ACCESS);
              })
              .catch(error => {
                processErrorResponse(error);
              })
              .finally(() => {
                dispatch(hideLoadingProgressBar());
                if (currentCompany?.id) {
                  dispatch(findValuationScalarOpinion(currentCompanyMeasurementDateId));
                }
              });
          },
        },
      ],
    });
  }, [
    currentCompany?.id,
    currentCompanyMeasurementDateId,
    dispatch,
    isApprovedForClients,
    processErrorResponse,
    showDialog,
    successNotification,
  ]);

  const handleSetPageActions = useCallback(() => {
    if (updatePageActions) {
      updatePageActions({
        mainAction: {
          id: 'save',
          label: SAVE_LABEL,
          isActive: !formState.isValid,
          callback: handleSave,
        },
        secondaryActions: [
          {
            id: 'edit-mode',
            label: TOGGLE_EDIT_MODE_LABEL,
            isActive: true,
            callback: () => {
              setEditMode(!editMode);
            },
          },
          {
            id: 'approve-for-client-access',
            label: isApprovedForClients ? MARK_AS_DRAFT_LABEL : APPROVE_FOR_CLIENT_ACCESS_LABEL,
            isActive: true,
            callback: handleApproveReportForClientAccess,
          },
        ],
      });
    }
  }, [
    updatePageActions,
    formState.isValid,
    handleSave,
    isApprovedForClients,
    handleApproveReportForClientAccess,
    setEditMode,
    editMode,
  ]);

  useEffect(() => {
    if (valuationMethodsDetailSheet?.yLength > 0) {
      setSpreadsheets([valuationMethodsDetailSheet]);
    }
  }, [valuationMethodsDetailSheet]);

  useEffect(() => {
    handleSetPageActions();
  }, [handleSetPageActions]);

  useEffect(() => {
    setFormState({ isValid: validateCells() });
  }, [cells, validateCells]);

  return !isLoading && valuationMethodsDetailSheetProps ? (
    <div data-tour="valuation-methods-detail-table">
      <ScalarSpreadsheet {...valuationMethodsDetailSheetProps} />
    </div>
  ) : (
    <GridSkeleton />
  );
};

export default ValuationMethodsDetailTable;
